"use client";

import { Button } from "@ethera/ui/button";
import { Switch } from "@ethera/ui/switch";
import { NEXT_PUBLIC_SIGN_UP_URL } from "@guru/shared/config/env.client";
import { useTranslation } from "@guru/shared/i18n";
import { cn } from "@guru/shared/utils/cn";
import {
  ArrowRight,
  Building2,
  Check,
  Clock,
  GitBranch,
  HardDrive,
  Lightbulb,
  Lock,
  MessageCircle,
  Rocket,
  Sprout,
} from "lucide-react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import type React from "react";
import { Fragment, useState } from "react";
import { AllowanceMeter, type MeterVariant } from "@/components/marketing/allowance-meter";
import { CountUp } from "@/components/marketing/count-up";
import { SpotlightCard } from "@/components/marketing/spotlight-card";
import { WhyGlyph, type WhyGlyphName } from "@/components/marketing/why-glyph";
import { ScrollReveal } from "@/components/public/scroll-reveal";

const tierIcon: Record<string, React.ComponentType<{ className?: string }>> = {
  lite: Sprout,
  scale: Rocket,
  custom: Building2,
};

/** Maps each ROI pillar (in order) to its illustration. */
const whyGlyphNames: WhyGlyphName[] = ["recovered", "time", "modular"];

/** Pulls the leading number out of a quota string ("1,500" → 1500); null for "Custom". */
function parseQuota(value: string | undefined): number | null {
  if (!value) return null;
  const digits = value.replace(/[^0-9]/g, "");
  return digits.length > 0 ? Number.parseInt(digits, 10) : null;
}

/** Splits a feature string's leading ✅/❌/⏳ marker into a tidy icon state so the list reads cleanly. */
function normaliseFeature(raw: string): { label: string; state: "yes" | "no" | "soon" } {
  const trimmed = raw.trim();
  if (trimmed.startsWith("✅")) return { label: trimmed.replace(/^✅\s*/u, ""), state: "yes" };
  if (trimmed.startsWith("❌")) return { label: trimmed.replace(/^❌\s*/u, ""), state: "no" };
  if (trimmed.startsWith("⏳")) return { label: trimmed.replace(/^⏳\s*/u, ""), state: "soon" };
  return { label: trimmed, state: "yes" };
}

const formatQuota = (n: number) => Math.round(n).toLocaleString("en-IN");

export function PricingSection() {
  const { t } = useTranslation();
  const reduced = !!useReducedMotion();
  const [isAnnual, setIsAnnual] = useState(false);
  const pricing = t.homepage.pricing;

  return (
    <section id="pricing" className="relative overflow-clip bg-background py-20 sm:py-24">
      {/* Ledger texture anchors the section in the product's world */}
      <div
        className="ledger-grid ledger-grid-fade pointer-events-none absolute inset-0"
        aria-hidden="true"
      />

      <div className="relative z-10 mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <ScrollReveal className="mb-6 text-center" threshold={0.3}>
          <span className="mb-4 inline-block rounded-full bg-brand/15 px-4 py-1.5 text-sm font-medium text-brand-deep">
            {pricing.launchBadge}
          </span>
          <h2 className="text-balance text-3xl font-bold tracking-tight text-foreground sm:text-4xl">
            {pricing.title}
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-pretty text-lg text-muted-foreground">
            {pricing.subtitle}
          </p>
        </ScrollReveal>

        {/* Annual Billing Toggle */}
        <ScrollReveal threshold={0.3} delay={50}>
          <div className="mb-10 flex items-center justify-center gap-4">
            <span
              className={cn(
                "text-sm",
                !isAnnual ? "font-semibold text-foreground" : "text-muted-foreground"
              )}
            >
              {pricing.annualBilling.monthlyLabel}
            </span>
            <Switch checked={isAnnual} onCheckedChange={setIsAnnual} />
            <span
              className={cn(
                "text-sm",
                isAnnual ? "font-semibold text-foreground" : "text-muted-foreground"
              )}
            >
              {pricing.annualBilling.annualLabel}
            </span>
            <span
              className={cn(
                "rounded-full bg-positive/15 px-2 py-1 text-xs font-medium text-positive transition-opacity duration-300",
                isAnnual ? "opacity-100" : "opacity-0"
              )}
            >
              {pricing.annualBilling.saveBadge}
            </span>
          </div>
        </ScrollReveal>

        {/* Pricing Grid */}
        <div className="grid items-stretch gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {pricing.tiers.map((tier, index) => {
            const isHighlighted = tier.highlighted;
            const isCustom = tier.isCustom;
            const Icon = tierIcon[tier.id] ?? Sprout;
            const quota = parseQuota(tier.whatsappLimit);
            const priceMain = isAnnual && tier.annualPrice ? tier.annualPrice : tier.price;

            return (
              <ScrollReveal
                key={tier.id}
                delay={index * 100}
                threshold={0.15}
                className={cn(isHighlighted && "lg:-translate-y-3")}
              >
                <SpotlightCard
                  className={cn(
                    "group flex h-full flex-col rounded-2xl border p-6 transition-all duration-300",
                    "hover:-translate-y-1.5 hover:shadow-xl",
                    isHighlighted
                      ? "border-2 border-brand bg-card shadow-lg"
                      : "border-border bg-card hover:border-brand/40",
                    isCustom && "border-dashed"
                  )}
                >
                  {/* Tier badge */}
                  {tier.badge && (
                    <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                      <span
                        className={cn(
                          "badge-flip inline-block whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold",
                          isHighlighted
                            ? "bg-brand text-brand-contrast"
                            : "bg-ink/10 text-brand-deep"
                        )}
                      >
                        {tier.badge}
                      </span>
                    </div>
                  )}

                  {/* Tier name + description */}
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand/10 text-brand-deep">
                      <Icon className="h-5 w-5" />
                    </span>
                    <h3 className="text-lg font-bold text-foreground">{tier.name}</h3>
                  </div>
                  <p className="mt-2 min-h-[2.5rem] text-sm leading-relaxed text-muted-foreground">
                    {tier.description}
                  </p>

                  {/* Animated allowance illustration */}
                  <AllowanceMeter variant={tier.id as MeterVariant} className="mt-4" />

                  {/* Scannable spec strip */}
                  <div className="mt-4 grid grid-cols-3 gap-2">
                    <SpecChip icon={MessageCircle} label={pricing.labels.messages}>
                      {quota !== null ? (
                        <CountUp value={quota} format={formatQuota} />
                      ) : (
                        tier.whatsappLimit
                      )}
                    </SpecChip>
                    <SpecChip icon={HardDrive} label={pricing.labels.storage}>
                      {tier.storageLimit ?? "—"}
                    </SpecChip>
                    <SpecChip icon={GitBranch} label={pricing.labels.branches}>
                      {tier.branchesIncluded ?? "—"}
                    </SpecChip>
                  </div>

                  {/* Price — crossfades when the billing toggle flips */}
                  <div className="mt-5 min-h-[3.5rem]">
                    <AnimatePresence mode="wait" initial={false}>
                      <motion.div
                        key={isAnnual ? "annual" : "monthly"}
                        initial={reduced ? false : { opacity: 0, y: 6 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={reduced ? { opacity: 0 } : { opacity: 0, y: -6 }}
                        transition={{ duration: reduced ? 0 : 0.22 }}
                      >
                        <span
                          className={cn(
                            "font-bold tracking-tight text-foreground",
                            isCustom ? "text-2xl" : "text-3xl"
                          )}
                        >
                          {priceMain}
                        </span>
                        {!isAnnual && tier.period && (
                          <span className="text-sm text-muted-foreground">{tier.period}</span>
                        )}
                        {isAnnual && tier.annualSavings && (
                          <span className="mt-1 block text-xs font-medium text-positive">
                            {tier.annualSavings}
                          </span>
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>

                  {/* Value framing */}
                  <p className="mt-3 min-h-[3rem] text-sm font-medium leading-relaxed text-brand-deep">
                    {tier.valueFraming}
                  </p>

                  {/* What's included */}
                  <div className="mt-4 flex-grow">
                    <p className="mb-3 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {pricing.labels.included}
                    </p>
                    <ul className="space-y-2.5">
                      {tier.features.map((feature) => {
                        const { label, state } = normaliseFeature(feature);
                        return (
                          <li
                            key={label}
                            className={cn(
                              "flex items-start gap-2.5 text-sm",
                              state === "yes" ? "text-foreground/80" : "text-muted-foreground"
                            )}
                          >
                            {state === "yes" ? (
                              <Check className="mt-0.5 h-4 w-4 shrink-0 text-positive" />
                            ) : state === "soon" ? (
                              <Clock className="mt-0.5 h-4 w-4 shrink-0 text-brand-deep" />
                            ) : (
                              <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                            )}
                            <span>{label}</span>
                          </li>
                        );
                      })}
                    </ul>
                  </div>

                  {/* CTA */}
                  <Button
                    className={cn(
                      "mt-6 w-full gap-2",
                      isHighlighted &&
                        "cta-glow bg-brand font-semibold text-brand-contrast hover:bg-brand/90"
                    )}
                    variant={isHighlighted ? "default" : "outline"}
                    asChild
                  >
                    <a href={tier.ctaLink ?? NEXT_PUBLIC_SIGN_UP_URL}>
                      <span className="whitespace-normal text-wrap">{tier.cta}</span>
                      <ArrowRight className="h-4 w-4 shrink-0" aria-hidden="true" />
                    </a>
                  </Button>

                  {tier.belowCta && (
                    <p className="mt-2 text-center text-xs text-muted-foreground">
                      {tier.belowCta}
                    </p>
                  )}
                </SpotlightCard>
              </ScrollReveal>
            );
          })}
        </div>

        {/* Upgrade path — an illustrated Lite → Scale → Custom journey */}
        <ScrollReveal delay={150} threshold={0.2}>
          <div className="mt-14 rounded-2xl border border-border bg-card p-6 md:p-8">
            <h3 className="text-center text-xl font-bold text-foreground">
              {pricing.payAsYouGo.title}
            </h3>
            <p className="mx-auto mt-2 max-w-2xl text-center text-sm text-muted-foreground">
              {pricing.payAsYouGo.note}
            </p>

            <div className="mt-8 flex flex-col items-stretch justify-center gap-3 sm:flex-row sm:items-center">
              {pricing.tiers.map((tier, index) => {
                const Icon = tierIcon[tier.id] ?? Sprout;
                const isMid = tier.highlighted;
                return (
                  <Fragment key={tier.id}>
                    <div
                      className={cn(
                        "flex items-center justify-center gap-2.5 rounded-xl border px-5 py-3",
                        isMid
                          ? "border-brand bg-brand/10 text-brand-deep"
                          : "border-border bg-background text-foreground"
                      )}
                    >
                      <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                      <span className="font-semibold">{tier.name}</span>
                    </div>
                    {index < pricing.tiers.length - 1 && (
                      <ArrowRight
                        className="mx-auto h-5 w-5 shrink-0 rotate-90 text-brand sm:rotate-0"
                        aria-hidden="true"
                      />
                    )}
                  </Fragment>
                );
              })}
            </div>

            <div className="mt-8 flex items-start gap-3 rounded-xl border border-brand/30 bg-brand/5 p-4">
              <Lightbulb className="mt-0.5 h-5 w-5 shrink-0 text-brand-deep" aria-hidden="true" />
              <div>
                <p className="font-semibold text-foreground">{pricing.payAsYouGo.spendCap.title}</p>
                <p className="text-sm text-muted-foreground">
                  {pricing.payAsYouGo.spendCap.description}
                </p>
              </div>
            </div>
          </div>
        </ScrollReveal>

        {/* Why this works */}
        <ScrollReveal delay={200} threshold={0.2}>
          <div className="mt-16">
            <h3 className="mb-8 text-center text-2xl font-bold text-foreground">
              {pricing.roiPositioning.title}
            </h3>

            <div className="grid gap-6 md:grid-cols-3">
              {pricing.roiPositioning.pillars.map((pillar, index) => (
                <SpotlightCard
                  key={pillar.title}
                  className="group flex flex-col rounded-2xl border border-border bg-card p-6 text-center transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl hover:border-brand/40"
                >
                  <WhyGlyph name={whyGlyphNames[index] ?? "recovered"} />
                  <h4 className="mt-5 font-semibold text-foreground">{pillar.title}</h4>
                  <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                    {pillar.description}
                  </p>
                </SpotlightCard>
              ))}
            </div>

            <p className="mx-auto mt-8 max-w-3xl text-center text-xs text-muted-foreground">
              {pricing.roiPositioning.featureGrowth}
            </p>
          </div>
        </ScrollReveal>
      </div>
    </section>
  );
}

interface SpecChipProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  children: React.ReactNode;
}

/** Compact at-a-glance spec tile under the allowance meter. */
function SpecChip({ icon: Icon, label, children }: SpecChipProps) {
  return (
    <div className="rounded-lg border border-border bg-background/60 px-2 py-2 text-center">
      <Icon className="mx-auto h-4 w-4 text-brand-deep" aria-hidden="true" />
      <p className="mt-1 text-sm font-bold leading-tight tabular-nums text-foreground">
        {children}
      </p>
      <p className="whitespace-nowrap text-[10px] uppercase tracking-wide text-muted-foreground">
        {label}
      </p>
    </div>
  );
}
